// file: src/pages/Login.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import EmailIcon from "../Icons/EmailIcon.jsx";
import LockIcon from "../Icons/LockIcon.jsx";
import { signInTowing } from "../auth/signInTowing";
import CarIcon from "../Icons/CarIcon.jsx";
import {
  useAnimation,
  motion,
  AnimatePresence,
  MotionConfig,
} from "framer-motion";
import {
  formEnter,
  fadeUp,
  staggerContainer,
  hoverRaise,
  hoverLiftButton,
} from "../Animations/motionpresets.js";
import DriftingGlow from "../Animations/DriftingGlow";
import PasswordCheckListLogin from "../auth/PasswordCheckListLogin.jsx";

/* Firebase compat helpers */
import { getAuth, onAuthStateChanged } from "firebase/auth";

/* Towing API helpers */
import { towingLogin, setTowingAuth } from "../api/towingApi.js";

/* ---------- Config ---------- */
const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#4D95F0";
const brandCircleStyle = {
  background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
  boxShadow: `0 8px 22px ${BLUE}33`,
};

/** HttpOnly cookie probe endpoint (cheap “am I logged in?”) */
const AUTH_PROBE_URL =
  "https://towing-portal-api.onrender.com/api/v1/do-not-tow/?limit=1";

/** How long to trust a successful/failed probe inside the same tab. */
const PROBE_TTL_MS = 30_000;

/** sessionStorage key for probe cache */
const PROBE_CACHE_KEY = "towing_probe_cache_v1";

/* ---------- Small utils ---------- */
function mapAuthError(err) {
  const code =
    err?.code ||
    err?.payload?.code ||
    err?.status ||
    (typeof err?.message === "string" && err.message.toLowerCase());

  const badCred =
    code === "auth/invalid-credential" ||
    code === "auth/wrong-password" ||
    code === "auth/user-not-found" ||
    code === 401 ||
    (typeof code === "string" &&
      (code.includes("invalid-credential") ||
        code.includes("wrong-password") ||
        code.includes("user-not-found")));

  if (badCred) return "Invalid email or password.";
  if (
    code === "auth/network-request-failed" ||
    String(code).includes("network")
  )
    return "Network error. Check your connection and try again.";
  return (
    err?.payload?.message ||
    err?.message ||
    "Could not sign in. Please try again."
  );
}

const rememberEmail = {
  get: () => localStorage.getItem("remember_email") || "",
  set: (email) => localStorage.setItem("remember_email", email),
  clear: () => localStorage.removeItem("remember_email"),
  exists: () => !!localStorage.getItem("remember_email"),
};

function getProbeCache() {
  try {
    const raw = sessionStorage.getItem(PROBE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function setProbeCache(ok) {
  try {
    sessionStorage.setItem(
      PROBE_CACHE_KEY,
      JSON.stringify({ ok: !!ok, ts: Date.now() })
    );
  } catch {}
}
function clearProbeCache() {
  try {
    sessionStorage.removeItem(PROBE_CACHE_KEY);
  } catch {}
}

/** Background probe with timeout, always resolves boolean. */
async function probeSession(timeoutMs = 2500) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(AUTH_PROBE_URL, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: ctl.signal,
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    return res.ok; // 200 => cookie valid
  } catch {
    return false; // network/timeout => do not block UI
  } finally {
    clearTimeout(t);
  }
}

/* ---------- Component ---------- */
export default function Login() {
  const nav = useNavigate();
  const { search } = useLocation();

  /** If you navigated here from logout, skip the probe once & clear cache. */
  const skipProbeOnce = new URLSearchParams(search).get("logout") === "1";
  if (skipProbeOnce) clearProbeCache();

  /** Auto-login is ONLY attempted if user chose “Remember me” previously. */
  const allowAutoLogin = rememberEmail.exists() && !skipProbeOnce;

  const [email, setEmail] = useState(rememberEmail.get());
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(rememberEmail.exists());
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(allowAutoLogin); // disable inputs just while we probe

  /** Loop guard so we never double-redirect. */
  const redirectedOnceRef = useRef(false);

  const controls = useAnimation();
  useEffect(() => {
    const id = requestAnimationFrame(() => controls.start("ready"));
    return () => cancelAnimationFrame(id);
  }, [controls]);

  /** Auto-login strategy:
   * 1) Try silent Firebase getIdToken(false) → towingLogin({remember:true}) to mint cookie.
   * 2) If that fails, fall back to short-TTL cookie probe.
   * Never block the page; only show a tiny banner.
   */
  useEffect(() => {
    // only try auto-login if user chose "Remember me"
    if (!allowAutoLogin) {
      setProbing(false);
      return;
    }
    setProbing(true);
    const auth = getAuth();
    const off = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // token refresh happens under the hood; this forces a quick check
        try {
          await user.getIdToken(false);
        } catch {}
        if (!redirectedOnceRef.current) {
          redirectedOnceRef.current = true;
          nav("/towing/dashboard", { replace: true });
        }
      }
      setProbing(false);
    });
    return () => off();
  }, [allowAutoLogin, nav]);

  useEffect(() => {
    if (!err) return;
    controls
      .start({
        x: [0, -10, 10, -8, 8, -4, 4, 0],
        transition: { duration: 0.45 },
      })
      .then(() => controls.set({ x: 0 }));
  }, [err, controls]);

  useEffect(() => {
    if (err && (email !== "" || pass !== "")) setErr("");
  }, [email, pass, err]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await signInTowing(email.trim(), pass, remember);
      if (remember) rememberEmail.set(email.trim());
      else rememberEmail.clear();
      nav("/towing/dashboard");
    } catch (errAny) {
      setErr(mapAuthError(errAny));
    } finally {
      setLoading(false);
    }
  }
  return (
    <MotionConfig reducedMotion="never">
      <div className="min-h-screen grid place-items-center relative overflow-hidden px-4 bg-[radial-gradient(700px_350px_at_0%_0%,rgba(186,230,253,.45),transparent_60%),radial-gradient(800px_380px_at_100%_100%,rgba(199,210,254,.45),transparent_60%),linear-gradient(180deg,#f8fafc,#eef2ff)]">
        <div className="pointer-events-none absolute inset-2 rounded-[24px] ring-1 ring-white/60 shadow-[inset_0_1px_0_rgba(255,255,255,.7)]" />

        <DriftingGlow
          className="absolute -top-24 -left-24 bg-sky-200/50"
          size={420}
        />
        <DriftingGlow
          className="absolute -bottom-28 -right-24 bg-indigo-200/50 blur-[90px]"
          size={520}
          dx={[0, -8, 8, 0]}
          dy={[0, -6, 10, 0]}
          duration={16}
        />

        <motion.form
          onSubmit={onSubmit}
          className="w-full max-w-md rounded-3xl border border-white/70 bg-white/80 backdrop-blur-xl p-6 sm:p-8 shadow-[0_20px_60px_rgba(67,56,202,.12),0_10px_30px_rgba(2,6,23,.06)]"
          variants={formEnter}
          animate={controls}
          initial="initial"
        >
          {/* Tiny banner instead of a blocking full-screen gate */}
          {probing && (
            <div className="mb-3 -mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Checking your session…
            </div>
          )}

          <motion.div
            variants={staggerContainer(0.12, 0.18)}
            initial="hidden"
            animate="show"
          >
            <motion.div
              className="mb-4 flex items-center gap-2"
              variants={fadeUp}
            >
              <span
                className="inline-grid place-items-center h-8 w-8 rounded-full text-white ring-1 ring-white/50"
                style={brandCircleStyle}
              >
                <CarIcon className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold text-slate-900">
                Towing Portal
              </span>
            </motion.div>

            <motion.div className="text-left" variants={fadeUp}>
              <div className="mb-1 text-[15px] font-bold text-black">
                Welcome back
              </div>
              <h1
                className="text-2xl font-extrabold leading-tight"
                style={{ color: BLUE }}
              >
                Login
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Sign in to access your towing portal.
              </p>
            </motion.div>

            <motion.label
              className="block mt-5 text-sm text-slate-700 text-left"
              variants={fadeUp}
            >
              <span className="font-bold">Email</span>
              <motion.div className="relative mt-2 group" {...hoverRaise}>
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-2 text-slate-900 placeholder-slate-400 outline-none font-normal focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60"
                  disabled={probing || loading}
                />
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                  <EmailIcon className="w-5 h-5" />
                </span>
              </motion.div>
            </motion.label>

            <motion.label
              className="block mt-4 text-sm text-slate-700 text-left"
              variants={fadeUp}
            >
              <span className="font-bold">Password</span>
              <motion.div className="relative mt-2 group" {...hoverRaise}>
                <input
                  type={show ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-12 py-2 text-slate-900 placeholder-slate-400 outline-none font-normal focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60"
                  disabled={probing || loading}
                />
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                  <LockIcon className="w-5 h-5" />
                </span>

                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center rounded-md px-2 py-1 text-slate-500 hover:text-slate-700 focus:outline-none cursor-pointer"
                  disabled={probing || loading}
                >
                  {show ? (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 3l18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10.58 10.72a3 3 0 004.12 4.12M9.88 5.09A9.76 9.76 0 0121 12c-1.8 3.6-5.4 6-9 6-1.34 0-2.63-.28-3.8-.8M6.18 6.18A9.76 9.76 0 003 12c1.8 3.6 5.4 6 9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </motion.div>
            </motion.label>

            <PasswordCheckListLogin
              value={pass}
              show={!!pass}
              defaultCollapsed
            />

            <motion.div
              className="mt-3 flex items-center justify-between"
              variants={fadeUp}
            >
              <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-slate-600 select-none cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-200"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={probing || loading}
                />
                <span>Remember me</span>
              </label>

              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  nav("/towing/forgot");
                }}
                className="text-xs sm:text-sm text-sky-600 hover:underline "
              >
                Forgot password?
              </a>
            </motion.div>

            <AnimatePresence>
              {err && (
                <motion.div
                  key="err"
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 26 }}
                  className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm"
                  aria-live="assertive"
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              type="submit"
              disabled={loading || probing}
              {...hoverLiftButton}
              variants={fadeUp}
              style={{ background: BLUE, boxShadow: `0 12px 28px ${BLUE}33` }}
              className="mt-4 w-full rounded-xl py-2.5 font-semibold text-white cursor-pointer transition hover:brightness-[1.04] active:translate-y-px disabled:opacity-70"
            >
              {loading ? "Signing in..." : "Sign in"}
            </motion.button>

            <motion.div
              variants={fadeUp}
              className="mt-5 pt-3 border-t border-slate-200 text-[11px] text-slate-500 text-left"
            >
              Protected by best-practice security. Need access? Contact your
              administrator.
            </motion.div>
          </motion.div>
        </motion.form>
      </div>
    </MotionConfig>
  );
}
